import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import blogData from '../data/blog.json';

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [activeTag, setActiveTag] = useState('all');
  const [tags, setTags] = useState([]);

  useEffect(() => {
    // Load blog posts
    setPosts(blogData);

    // Extract unique tags
    const allTags = blogData.reduce((acc, post) => {
      post.tags.forEach(tag => {
        if (!acc.includes(tag)) {
          acc.push(tag);
        }
      });
      return acc;
    }, []);

    setTags(allTags);
  }, []);

  const filteredPosts = activeTag === 'all' 
    ? posts 
    : posts.filter(post => post.tags.includes(activeTag));

  return (
    <BlogContainer>
      <BlogHeader>
        <Title
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          Blog
        </Title>
        <Subtitle>Thoughts, tutorials, and insights</Subtitle>
        
        <TagFilter>
          <TagButton
            $active={activeTag === 'all'}
            onClick={() => setActiveTag('all')}
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.95 }}
          >
            All
          </TagButton>
          {tags.map(tag => (
            <TagButton
              key={tag}
              $active={activeTag === tag}
              onClick={() => setActiveTag(tag)}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              {tag}
            </TagButton>
          ))}
        </TagFilter>
      </BlogHeader>

      <BlogGrid>
        {filteredPosts.map((post) => (
          <BlogPostCard
            key={post.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.5 }}
          >
            <PostImageContainer>
              <PostImage src={post.image} alt={post.title} />
            </PostImageContainer>
            
            <PostContent>
              <PostDate>{new Date(post.date).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}</PostDate>
              <PostTitle>{post.title}</PostTitle>
              <PostExcerpt>{post.excerpt}</PostExcerpt>
              
              <TagsContainer>
                {post.tags.map((tag, index) => (
                  <Tag key={index} onClick={() => setActiveTag(tag)}>#{tag}</Tag>
                ))}
              </TagsContainer>
              
              <ReadMoreLink to={`/blog/${post.slug}`}>
                Read More →
              </ReadMoreLink>
            </PostContent>
          </BlogPostCard>
        ))}
      </BlogGrid>
    </BlogContainer>
  );
};

const BlogContainer = styled.div`
  padding: 120px 20px 50px;
  max-width: 1200px;
  margin: 0 auto;
`;

const BlogHeader = styled.div`
  text-align: center;
  margin-bottom: 50px;
`;

const Title = styled(motion.h1)`
  font-size: 3rem;
  color: var(--accent);
  margin-bottom: 10px;
`;

const Subtitle = styled.h2`
  font-size: 1.2rem;
  color: var(--text-secondary);
  margin-bottom: 30px;
  font-weight: 400;
`;

const TagFilter = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 30px;
`;

const TagButton = styled(motion.button)`
  padding: 0.5rem 1rem;
  border: 1px solid ${props => props.$active ? 'var(--accent)' : 'rgba(255, 255, 255, 0.1)'};
  background: ${props => props.$active ? 'rgba(100, 255, 218, 0.1)' : 'transparent'};
  color: ${props => props.$active ? 'var(--accent)' : 'var(--text-primary)'};
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
  }
`;

const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const BlogPostCard = styled(motion.article)`
  background: var(--secondary);
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 10px 30px -15px rgba(2, 12, 27, 0.7);
  transition: transform 0.2s;
  height: 100%;
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const PostImageContainer = styled.div`
  width: 100%;
  height: 200px;
  overflow: hidden;
`;

const PostImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
  
  ${BlogPostCard}:hover & {
    transform: scale(1.05);
  }
`;

const PostContent = styled.div`
  padding: 1.5rem;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const PostDate = styled.p`
  font-size: 0.9rem;
  color: var(--text-secondary);
  margin-bottom: 10px;
  font-family: 'SF Mono', 'Fira Code', monospace;
`;

const PostTitle = styled.h3`
  font-size: 1.5rem;
  color: var(--text-primary);
  margin-bottom: 10px;
  line-height: 1.3;
`;

const PostExcerpt = styled.p`
  color: var(--text-secondary);
  margin-bottom: 20px;
  line-height: 1.6;
  flex-grow: 1;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 20px;
`;

const Tag = styled.span`
  font-size: 0.8rem;
  color: var(--accent);
  cursor: pointer;
  transition: var(--transition);
  
  &:hover {
    text-decoration: underline;
  }
`;

const ReadMoreLink = styled(Link)`
  align-self: flex-start;
  color: var(--accent);
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 0.9rem;
  text-decoration: none;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    width: 0;
    height: 1px;
    bottom: -2px;
    left: 0;
    background-color: var(--accent);
    transition: width 0.3s;
  }
  
  &:hover:after {
    width: 100%;
  }
`;

export default Blog;